import {
  Component,
  inject,
  OnInit,
  HostListener,
  ElementRef,
  viewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericService } from '../services/generic.service';
import { AI_SKILLS_LABEL } from '../shared/constants';
import { IResumePreviewResponse, IResumePreviewSetting, IResumePreviewSettingField } from './resume-preview.model';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { SidebarComponent } from './sidebar/sidebar.component';
import { ContentHeaderComponent } from './content-header/content-header.component';
import { AcademicComponent } from './academic/academic.component';
import { ExperienceComponent } from './experience/experience.component';
import { SkillsComponent } from './skills/skills.component';
import { ApplicationHistoryComponent } from './application-history/application-history.component';

@Component({
    selector: 'app-resume-quick-link',
    templateUrl: './resume-quick-link.component.html',
    styleUrl: './resume-quick-link.component.scss',
    imports: [NzGridModule, NzAvatarModule, SidebarComponent, ContentHeaderComponent, AcademicComponent, ExperienceComponent, SkillsComponent, ApplicationHistoryComponent]
})
export class ResumeQuickLinkComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly genericService = inject(GenericService);
	private readonly ngxLoader = inject(NgxUiLoaderService);
	private readonly nzMessageService = inject(NzMessageService);
	public userData: IResumePreviewResponse;
	public skillsData: any;
	private resumePreviewId: number;
	public latestJobTitle: string;
	public isFirstNameView: boolean;
	public isLastNameView: boolean;

	public headerSetting: IResumePreviewSetting | undefined;
	public academicSetting: IResumePreviewSetting | undefined;
	public experienceSetting: IResumePreviewSetting | undefined;
	public sidebarSetting: IResumePreviewSetting | undefined;
	public skillSetting: IResumePreviewSetting | undefined;
	public AIskillSetting: IResumePreviewSetting | undefined;
	public applicationSetting: IResumePreviewSetting | undefined;
	public isExpired: boolean;
	public isExpLabelShow: boolean;

	private screenWidth: number;
	public mobile = false;
	private readonly forMobileSize = 768;

	readonly headerShow = viewChild<ElementRef>('hideShowHeader');
	public isHeaderVisible = false;

	ngOnInit(): void {
		this.resumePreviewId = this.route.snapshot.params['guid'];
		this.screenWidth = window.innerWidth;
		this.mobile = this.screenWidth <= this.forMobileSize;
		this.ngxLoader.start();
		this.genericService
			.get(`Candidate/GetJxResumePreview/${this.resumePreviewId}`)
			.subscribe({
				next: (setData: IResumePreviewResponse) => {
					this.ngxLoader.stop();

					if (setData.statusCode == 404 || setData.statusCode == 410) {
						this.isExpired = true;
						return;
					}

					this.userData = setData;
					this.latestJobTitle = this.getLatestJobTitle(
						setData.data.experience.workHistories
					);

					this.headerSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Header'
					)[0];
					this.academicSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Academic'
					)[0];
					this.experienceSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Experience'
					)[0];
					this.sidebarSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Sidebar'
					)[0];
					this.skillSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Skill'
					)[0];
					this.AIskillSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'AI Skill'
					)[0];
					this.applicationSetting = setData.settings.filter(
						(e: IResumePreviewSetting) => e.name == 'Application'
					)[0];

					this.skillsData = setData?.data?.skills?.map((e: any) => ({
						category: e.category,
						skills: e.skills?.map((b: any) => ({
							skill: b.skill,
							proficiencyRate: this.proficiencyFormat(b.proficiency, 0),
							proficiencyLabel: this.proficiencyFormat(b.proficiency, 1),
							yearOfExperience: b.yearOfExperience,
							description: b.description
						}))
					}))?.sort((a: any, b: any) => {
						if (a.category === AI_SKILLS_LABEL) return -1;
						if (b.category === AI_SKILLS_LABEL) return 1;
						return 0;
					});

					this.isFirstNameView = this.headerSetting.fields
						?.filter((e: IResumePreviewSettingField) => e.name == 'First Name')
						.map((e: IResumePreviewSettingField) => e.active)[0] ?? false;
					this.isLastNameView = this.headerSetting.fields
						?.filter((e: IResumePreviewSettingField) => e.name == 'Last Name')
						.map((e: IResumePreviewSettingField) => e.active)[0] ?? false;

					this.isExpLabelShow = !Object.values(setData.data.experience)?.every(
						(arr: any) => arr?.length === 0
					);
				},
				error: (error) => {
					this.ngxLoader.stop();
					this.nzMessageService.error(error);
				}
			});
	}

	proficiencyFormat(data: string, index: number) {
		return data?.split(' - ')[index];
	}

	getLatestJobTitle(workHistories: any[]): string {
		const sortedHistories = workHistories?.sort(
			(a, b) =>
				new Date(b.jobDurationDateFrom).getTime() -
				new Date(a.jobDurationDateFrom).getTime()
		);

		return sortedHistories?.length > 0 ? sortedHistories[0].jobTitle : '';
	}

	@HostListener('window:resize')
	onWindowResize() {
		this.screenWidth = window.innerWidth;
		this.mobile = this.screenWidth <= this.forMobileSize;
	}

	ngAfterViewInit() {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.isHeaderVisible = true;
				} else {
					this.isHeaderVisible = false;
				}
			});
		});

		const el = this.headerShow()?.nativeElement;
		if (el) {
			observer.observe(el);
		}
	}
}
